import _ from 'lodash';
import * as React from 'react';
import cn from 'classnames';
import styles from './work-item-details.module.css';

import {
  Tabset,
  TabList,
  NavTab,
  TabContent,
  TabPanel,
} from '../../components/tabs/Tabs';
import Accordion, {
  AccordionPanelProps,
} from '../../components/accordion/Accordion';

import { WaiverWorkItemChildImageResult } from '../../types/waiverWorkItem';

interface WaiverWebsiteChangeProps {
  websiteUrl?: string;
  newImageData?: string;
  originalImageData?: string;
  diffImageData?: string;
  childResults?: WaiverWorkItemChildImageResult[];
}

const WaiverWebsiteChange: React.FC<WaiverWebsiteChangeProps> = (props) => {
  const parent: AccordionPanelProps = {
    id: 'parent',
    title: <Title title="Main Screenshot" websiteUrl={props.websiteUrl} />,
    show: true,
    children: (
      <WebsiteImageDiff
        originalImageData={props.originalImageData}
        newImageData={props.newImageData}
        diffImageData={props.diffImageData}
      />
    ),
  };
  const children: AccordionPanelProps[] = (props.childResults || []).map(
    (result, index) => {
      return {
        id: `child${index}`,
        title: (
          <Title
            title={`Child Link Screenshot #${index + 1}`}
            websiteUrl={result.websiteUrl}
          />
        ),
        show: false,
        children: (
          <WebsiteImageDiff
            originalImageData={result.originalImageData}
            newImageData={result.newImageData}
            diffImageData={result.diffImageData}
          />
        ),
      };
    },
  );
  return (
    <div className={cn(styles.websiteChange)}>
      <Accordion id="websiteChanges" panels={_.concat([parent], children)} />
    </div>
  );
};

interface TitleProps {
  title: string;
  websiteUrl?: string;
}

const Title: React.FC<TitleProps> = (props) => (
  <div>
    <div className={cn(styles.websiteChangeTitleText)}>{props.title}</div>
    {props.websiteUrl ? (
      <a
        title="View Website"
        target="_blank"
        rel="noopener noreferrer"
        className={cn(styles.workItemLink, styles.websiteChangeLink)}
        href={props.websiteUrl}
      >
        View Airline Website
      </a>
    ) : (
      ''
    )}
  </div>
);

interface WebsiteImageDiffProps {
  newImageData?: string;
  originalImageData?: string;
  diffImageData?: string;
  showDiff?: boolean;
}

interface State {
  showDiff: boolean;
}

class WebsiteImageDiff extends React.Component<WebsiteImageDiffProps, State> {
  state: State = {
    showDiff: this.props.showDiff || false,
  };

  hasChanges = (): boolean =>
    !!this.props.originalImageData ||
    !!this.props.newImageData ||
    !!this.props.diffImageData;

  showDiff = (value: boolean): void => this.setState({ showDiff: value });

  render() {
    if (!this.hasChanges()) {
      return (
        <div className={cn('')}>
          <div className={cn('alert', 'alert-warning')}>
            No differences detected.
          </div>
        </div>
      );
    }

    return (
      <Tabset style={styles.websiteChangeContent}>
        <TabList>
          <NavTab
            name="Before & After"
            active={!this.state.showDiff}
            onSelect={() => this.showDiff(false)}
            invertColors={true}
          />
          <NavTab
            name="Differences"
            active={this.state.showDiff}
            onSelect={() => this.showDiff(true)}
            invertColors={true}
          />
        </TabList>
        <TabContent>
          <TabPanel active={!this.state.showDiff}>
            <div className={cn('row')}>
              <WebsiteImage
                imageData={this.props.originalImageData}
                styles={['col-md-6']}
                type="Original"
              />
              <WebsiteImage
                imageData={this.props.newImageData}
                styles={['col-md-6']}
                type="New"
              />
            </div>
          </TabPanel>
          <TabPanel active={this.state.showDiff}>
            <div className={cn('row')}>
              <WebsiteImage
                imageData={this.props.diffImageData}
                styles={['col-md-12']}
                type="Differences"
              />
            </div>
          </TabPanel>
        </TabContent>
      </Tabset>
    );
  }
}

interface WebsiteImageProps {
  imageData?: string;
  styles: string[];
  type: string;
}

const WebsiteImage: React.FC<WebsiteImageProps> = (props) => {
  if (!props.imageData) {
    return (
      <div className={cn('alert', 'alert-warning')}>
        No {props.type} image available
      </div>
    );
  }
  const imageSrc = 'data:image/png;base64, ' + props.imageData;
  return (
    <img
      className={cn(props.styles, styles.websiteChangeImage)}
      src={imageSrc}
    />
  );
};

export default WaiverWebsiteChange;
